/**
 * The avatar and its menu — profile, sign out.
 *
 * Lives in two places now. A super admin still has the navy context bar; everyone else
 * belongs to exactly one organisation, so that bar was removed and this sits in the tab
 * row instead. `tone` is the difference: white-on-navy, or dark on the light strip.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { SignOut, User } from '@phosphor-icons/react';
import { ROLE_LABELS } from './moduleRegistry';

export default function UserMenu({ tone = 'dark', size = 'md' }) {
  const { user, logout, tenantId, tenantInfo } = useAuth();
  const navigate = useNavigate();
  const dark = tone === 'dark';

  const handleLogout = () => {
    const tenantSubdomain = localStorage.getItem('lms_tenant_subdomain') || tenantInfo?.subdomain;
    const userTenantId = user?.tenant_id || tenantId;
    let redirectUrl = '/login';
    if (user?.role !== 'super_admin' && userTenantId && tenantSubdomain) {
      redirectUrl = `/tenant/${tenantSubdomain}/login`;
    }
    logout();
    window.location.href = redirectUrl;
  };

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();
  const box = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  const inner = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost"
          className={`${box} rounded-full p-0 ${dark ? 'hover:bg-white/10' : 'hover:bg-muted'}`}
          data-testid="user-menu-trigger">
          <Avatar className={`${inner} ${dark ? 'ring-2 ring-white/60' : 'ring-1 ring-border'}`}>
            <AvatarFallback className={`font-semibold text-xs ${dark ? 'bg-white text-shell-topbar' : 'bg-shell-primary text-white'}`}>
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs leading-none text-muted-foreground">{user?.email || user?.phone}</p>
            {/* The organisation and role used to be chips across the top. For someone who
                only ever belongs to one, they belong here — out of the way but findable. */}
            {user?.role !== 'super_admin' && tenantInfo?.name && (
              <p className="text-xs leading-none text-muted-foreground mt-1">{tenantInfo.name}</p>
            )}
            <p className="text-xs leading-none text-muted-foreground mt-1">{ROLE_LABELS[user?.role]}</p>
            {user?.member_id && (
              <p className="text-xs leading-none text-muted-foreground mt-1 font-mono">{user.member_id}</p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
          <User className="mr-2 h-4 w-4" /> Profile settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
          <SignOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
